import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, RefreshCw } from "lucide-react";

const SOURCING_URL = process.env.REACT_APP_SOURCING_API_URL;

const STATUS_TABS = ["ALL", "sent", "pending", "failed"];

// Class strings rather than inline colours: the light pastels have no readable dark
// equivalent, and only a `dark:` variant can follow the theme without re-rendering on it.
const STATUS_STYLE = {
    sent: "bg-green-100 text-green-800 dark:bg-green-500/15 dark:text-green-300",
    pending: "bg-yellow-100 text-yellow-800 dark:bg-amber-500/15 dark:text-amber-300",
    failed: "bg-red-100 text-red-800 dark:bg-red-500/15 dark:text-red-300",
};
const STATUS_STYLE_FALLBACK = "bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300";

// Shared by the header row and every card, so the two can't drift out of alignment.
const GRID = "grid grid-cols-[2fr_3fr_90px_130px_130px] gap-3";

function fmtDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

const EmailTracker = () => {
    const navigate = useNavigate();
    const orgId = localStorage.getItem("current_org_id");

    const [allEmails, setAllEmails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("ALL");
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    const fetchEmails = async (showSpinner = true) => {
        if (showSpinner) setLoading(true);
        else setRefreshing(true);
        setError(null);
        try {
            const { data } = await axios.get(`${SOURCING_URL}/email/org-emails`, { params: { org_id: orgId } });
            setAllEmails(data);
        } catch (e) {
            const detail = e.response?.data?.detail;
            const msg = Array.isArray(detail)
                ? detail.map(d => `${d.loc?.slice(-1)[0]}: ${d.msg}`).join(" | ")
                : (detail || e.message || "Failed to load emails");
            setError(String(msg));
            setAllEmails([]);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => { fetchEmails(); }, []);

    const emails = activeTab === "ALL" ? allEmails : allEmails.filter(e => e.status === activeTab);

    const counts = allEmails.reduce((acc, e) => {
        acc[e.status] = (acc[e.status] || 0) + 1;
        return acc;
    }, {});

    return (
        <div className="min-h-screen bg-bgSecondary dark:bg-none">
            {/* Header */}
            <div className="bg-bgPrimary dark:bg-transparent border-b border-gray-100 dark:border-gray-700/50 px-6 py-4 flex items-center gap-4">
                <button
                    onClick={() => navigate(-1)}
                    className="flex text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                >
                    <ArrowLeft size={20} />
                </button>
                <h1 className="m-0 flex-1 text-[15px] font-semibold text-textPrimary dark:text-white">Email Tracker</h1>
                <button
                    onClick={() => fetchEmails(false)}
                    disabled={refreshing}
                    className="flex items-center gap-1.5 text-[13px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 disabled:opacity-50 transition-colors"
                >
                    <RefreshCw size={15} className={refreshing ? "animate-spin" : ""} />
                    Refresh
                </button>
            </div>

            <div className="max-w-[900px] mx-auto px-6 py-6">
                {/* Summary pills */}
                <div className="flex flex-wrap gap-2.5 mb-5">
                    {STATUS_TABS.map(tab => {
                        const count = tab === "ALL" ? allEmails.length : (counts[tab] || 0);
                        const isActive = activeTab === tab;
                        return (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-4 py-1.5 rounded-full text-[13px] transition-colors ${
                                    isActive
                                        ? "font-semibold text-white bg-gradient-to-r from-pink-500 to-blue-500"
                                        : "border border-gray-200 dark:border-gray-600 bg-bgPrimary dark:bg-transparent text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500"
                                }`}
                            >
                                {tab === "ALL" ? "All" : tab.charAt(0).toUpperCase() + tab.slice(1)} {count > 0 && `(${count})`}
                            </button>
                        );
                    })}
                </div>

                {/* Table */}
                {loading ? (
                    <div className="flex justify-center py-16">
                        <div className="w-7 h-7 border-2 border-purple-400 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : error ? (
                    <div className="text-center py-12">
                        <p className="text-sm text-red-500 dark:text-red-400 mb-2">Error: {error}</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500">org_id in use: {orgId || "null (not set in localStorage)"}</p>
                    </div>
                ) : emails.length === 0 ? (
                    <div className="text-center py-16 text-sm text-gray-400 dark:text-gray-500">
                        {allEmails.length === 0
                            ? `No emails found for org: ${orgId}`
                            : `No ${activeTab} emails.`}
                    </div>
                ) : (
                    <div className="flex flex-col gap-2">
                        {/* Column headers */}
                        <div className={`${GRID} px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400 dark:text-gray-500`}>
                            <span>To</span>
                            <span>Subject</span>
                            <span>Status</span>
                            <span>Scheduled</span>
                            <span>Sent</span>
                        </div>

                        {emails.map(email => {
                            const badge = STATUS_STYLE[email.status] || STATUS_STYLE_FALLBACK;
                            const isFailed = email.status === "failed";
                            return (
                                <div
                                    key={email.id}
                                    className={`rounded-xl border px-4 py-3 flex flex-col gap-1.5 ${
                                        isFailed
                                            ? "bg-red-50/60 dark:bg-red-500/10 border-red-200 dark:border-red-500/30"
                                            : "bg-bgPrimary dark:bg-white/5 border-gray-200 dark:border-gray-700/50"
                                    }`}
                                >
                                    <div className={`${GRID} items-center`}>
                                        <span className="text-[13px] text-gray-700 dark:text-gray-300 truncate" title={email.to_address}>
                                            {email.to_address}
                                        </span>
                                        <span className="text-[13px] text-gray-500 dark:text-gray-400 truncate" title={email.subject}>
                                            {email.subject}
                                        </span>
                                        <span className={`inline-block w-fit whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-semibold ${badge}`}>
                                            {email.status}
                                        </span>
                                        <span className="text-xs text-gray-400 dark:text-gray-500">{fmtDate(email.scheduled_at)}</span>
                                        <span className="text-xs text-gray-400 dark:text-gray-500">{fmtDate(email.sent_at)}</span>
                                    </div>
                                    {isFailed && email.error_message && (
                                        <div className="flex items-start gap-1.5 pt-1 border-t border-red-200 dark:border-red-500/30">
                                            <span className="text-[11px] font-semibold text-red-500 dark:text-red-400 whitespace-nowrap">Error:</span>
                                            <span className="text-[11px] leading-relaxed text-red-700 dark:text-red-300">{email.error_message}</span>
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default EmailTracker;
